import { Size, useThree } from '@react-three/fiber';
import {
    CSSProperties,
    FC,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useEvent } from 'react-use';
import { Object3D, Vector2, Vector3 } from 'three';
import { SelectionBox } from './selectionBoxRaw.js';

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const getCoords = (clientX: number, clientY: number, size: Size) => [
    ((clientX - size.left) / size.width) * 2 - 1,
    -((clientY - size.top) / size.height) * 2 + 1,
];

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const getViewportCoords = (
    clientX: number,
    clientY: number,
    size: Size
) => [(clientX / size.width) * 2 - 1, -(clientY / size.height) * 2 + 1];

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const getClientCoords = (
    positionX: number,
    positionY: number,
    size: Size
) => [
    ((positionX + 1) / 2) * size.width + size.left,
    Math.abs((positionY - 1) / 2) * size.height + size.top,
];

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const setSelectedStyle = (collection: any[], selected: boolean) => {
    // eslint-disable-next-line no-restricted-syntax
    for (const item of collection) {
        if (item.material) {
            item.material.linewidth = selected ? 3 : 1;
        }
    }
};

interface SelectionProps {
    onSelectionChanged?(
        objects: Object3D[],
        start_point?: Vector3,
        end_point?: Vector3
    ): void;
    style?: CSSProperties;
}

export const Selection: FC<SelectionProps> = ({
    style,
    onSelectionChanged,
}) => {
    const { camera, scene, gl, size } = useThree();
    const [start, setStart] = useState<Vector2>();
    const [mouse, setMouse] = useState<[number, number]>();
    const [isSelecting, setIsSelecting] = useState<boolean>(false);
    const [selection, setSelection] = useState<Object3D[]>([]);
    const [endPoint, setSavedEndPoint] = useState<Vector3>();
    const [startPoint, setSavedStartPoint] = useState<Vector3>();

    const selectRectangle = useRef(document.createElement('div'));

    useEffect(() => {
        onSelectionChanged?.call(null, selection, startPoint, endPoint);
    }, [selection]);

    useEffect(() => {
        selectRectangle.current.classList.add('selectBox');
        selectRectangle.current.style.pointerEvents = 'none';
        // eslint-disable-next-line no-restricted-syntax, guard-for-in
        for (const key in style) {
            const val = (style as any)[key];
            selectRectangle.current.style.setProperty(
                key
                    .replace(/([a-z])([A-Z])/g, '$1-$2')
                    .replace(/[\s_]+/g, '-')
                    .toLowerCase(),
                val
            );
        }
    }, [selectRectangle, style]);

    useEffect(() => {
        if (isSelecting && start && mouse) {
            gl.domElement.parentElement?.append(selectRectangle.current);

            const topLeft = {
                x: Math.min(start.x, mouse[0]),
                y: Math.min(start.y, mouse[1]),
            };
            const bottomRight = {
                x: Math.max(start.x, mouse[0]),
                y: Math.max(start.y, mouse[1]),
            };

            selectRectangle.current.style.left = `${topLeft.x}px`;
            selectRectangle.current.style.top = `${topLeft.y}px`;
            selectRectangle.current.style.width = `${
                bottomRight.x - topLeft.x
            }px`;
            selectRectangle.current.style.height = `${
                bottomRight.y - topLeft.y
            }px`;
        } else {
            selectRectangle.current.parentElement?.removeChild(
                selectRectangle.current
            );
        }
    }, [isSelecting, gl, start, mouse, selectRectangle]);

    const selectionBox = useMemo(
        () => new SelectionBox(camera, scene),
        [scene, camera]
    );

    const appendSelection = useCallback(
        (toAppend: any[]) => {
            setSelection([...selection, ...toAppend]);
        },
        [selection]
    );

    const onPointerDown = useCallback(
        (e: Event) => {
            const event = e as PointerEvent;
            const { clientX, clientY, altKey, ctrlKey, button } = event;
            if (!altKey && !isSelecting && !button) {
                const [startX, startY] = getCoords(clientX, clientY, size);
                setStart(new Vector2(clientX, clientY));
                setIsSelecting(true);
                if (!ctrlKey) {
                    setSelectedStyle(selection, false);
                }
                selectionBox.startPoint.set(startX, startY, 0.5);
                selectionBox.endPoint.set(startX, startY, 0.5);
                setSavedStartPoint(() =>
                    new Vector3().copy(selectionBox.startPoint)
                );
                setSavedEndPoint(() =>
                    new Vector3().copy(selectionBox.endPoint)
                );
            }
        },
        [
            isSelecting,
            selection,
            selectionBox.endPoint,
            selectionBox.startPoint,
            size,
        ]
    );

    const onPointerMove = useCallback(
        (e: Event) => {
            if (!isSelecting) return;
            const { clientX, clientY } = e as PointerEvent;
            const [endX, endY] = getCoords(clientX, clientY, size);
            setMouse([clientX, clientY]);
            selectionBox.select();
            setSelectedStyle(selectionBox.collection, false);

            selectionBox.endPoint.set(endX, endY, 0.5);
            selectionBox.select();

            setSelectedStyle(selectionBox.collection, true);
        },
        [isSelecting, selectionBox, size]
    );

    const onPointerUp = useCallback(
        (e: Event) => {
            const { ctrlKey, clientX, clientY, button } = e as PointerEvent;

            if (isSelecting || !button) {
                setIsSelecting(false);

                const [endX, endY] = getCoords(clientX, clientY, size);
                selectionBox.endPoint.set(endX, endY, 0.5);
                const curSelected = selectionBox.select();

                setSavedEndPoint(() => new Vector3(endX, endY, 0.5));
                setMouse(undefined);
                setStart(undefined);

                if (ctrlKey) {
                    appendSelection(curSelected);
                } else {
                    setSelection(curSelected);
                }

                setSelectedStyle(selectionBox.collection, true);
            }
        },
        [isSelecting, size, selectionBox, appendSelection]
    );

    useEvent('pointerdown', onPointerDown, gl.domElement);
    useEvent('pointermove', onPointerMove, gl.domElement);
    useEvent('pointerup', onPointerUp, gl.domElement);

    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <></>;
};
